import React from 'react';
import { Illustration, NoDataFound } from '@fabric-ui/components';
import { createFileRoute, Navigate, Outlet, useSearch } from '@tanstack/react-router';
import { getTitleContextSlice } from '@utils/breadcrumbsUtils';
import { EntityProjectionName } from '@utils/entityProjectionNames';
import { CodeOwner } from '@utils/router.utils';
import { useGetAllRiskScoringConfig } from '@views/RiskScore/hooks';
import { RiskScoreProvider } from '@views/RiskScore/RiskScoreContext';
import { useCanReadAnyRiskScore } from '@views/RiskScore/useRiskScorePermissions';

const AccessDenied = () => (
  <NoDataFound
    headline="Access Denied"
    description={
      <>
        Unavailable due to missing permissions.
        <br />
        Contact your admin to request access.
      </>
    }
    img={<Illustration.Medium.Issue />}
    style={{ titleStyle: { fontSize: '24px', mb: 0 } }}
  />
);

const RiskScoreLayout = () => {
  const canReadAny = useCanReadAnyRiskScore();
  const { data, refetch } = useGetAllRiskScoringConfig({ enabled: canReadAny });
  const { scoreType } = useSearch({ strict: false }) as { scoreType: EntityProjectionName };

  if (!canReadAny) {
    return <AccessDenied />;
  }
  if (!data) {
    return null;
  }
  if (data.projectionToRiskScores.length === 0) {
    return <AccessDenied />;
  }

  const isValidScoreType = data.projectionToRiskScores.some(({ projectionName }) => projectionName === scoreType);

  if (!isValidScoreType) {
    const fallbackScoreType = data.projectionToRiskScores[0]?.projectionName as EntityProjectionName;
    return <Navigate to="." search={{ scoreType: fallbackScoreType }} replace />;
  }

  return (
    <RiskScoreProvider data={data} riskScoreProjectionName={scoreType} refetch={refetch}>
      <Outlet />
    </RiskScoreProvider>
  );
};

export const Route = createFileRoute('/_breadcrumbsLayout/$accountId/platform/risk-score')({
  beforeLoad: () => getTitleContextSlice('Risk Score', { preserveSearchParams: true }),
  staticData: {
    owner: CodeOwner.Uvm,
  },
  component: RiskScoreLayout,
});
